import React, { useState } from 'react'

import ComboboxMulti from '@shared/combobox_multi'

const sameId = (a, b) => String(a) === String(b)
const idsOf  = technicians => new Set(technicians.map(technician => String(technician.id)))

const Chip = ({ label, selected, onClick }) => (
  <span
    className = {`autocomplete__shortcut ${selected ? 'autocomplete__shortcut--selected' : ''}`}
    onClick   = {onClick}
  >
    {label}
  </span>
)

const Row = ({ label, className, children }) => (
  <div className={`timesheet__filters__row ${className || ''}`}>
    <div className="timesheet__filters__label">{label}</div>
    <div className="timesheet__filters__control">{children}</div>
  </div>
)

const Chips = ({ children }) => <div className="timesheet__filters__chips">{children}</div>

// A chip switched off only removes the technicians no other lit chip still covers.
const TechnicianFilters = props => {
  const partners       = props.partners || []
  const teams          = props.teams.accountTeams
  const allTechnicians = props.teams.allTechs.values
  const types          = [{ id: '', name: I18n.t("users.timesheets.event_types.all") }, ...(props.types || [])]

  const techniciansOfPartner = partner => {
    const attached = new Set(partner.technicianIds.map(String))
    return allTechnicians.filter(technician => attached.has(String(technician.id)))
  }

  const [selectedPartners, setSelectedPartners] = useState(
    new Set((props.partnerIds || []).map(String).filter(id => partners.some(partner => sameId(partner.id, id))))
  )
  const [selectedTeams, setSelectedTeams] = useState(new Set())
  const [value,         setValue]         = useState(props.technicians || [])
  const [type,          setType]          = useState(props.type || '')
  const tripsEnabled = props.tripsEnabled !== false
  const [trips,         setTrips]         = useState(tripsEnabled && props.trips !== false)

  const coveredBy = (partnerIds, teamIndexes) => {
    const covered = new Set()
    partners.filter(partner => partnerIds.has(String(partner.id)))
      .forEach(partner => techniciansOfPartner(partner).forEach(technician => covered.add(String(technician.id))))
    teams.filter((_, index) => teamIndexes.has(index))
      .forEach(team => team.values.forEach(technician => covered.add(String(technician.id))))
    return covered
  }

  const add = technicians => {
    const present = idsOf(value)
    const merged  = [...value]
    technicians.forEach(technician => {
      const id = String(technician.id)
      if (present.has(id)) return
      present.add(id)
      merged.push(technician)
    })
    const order = allTechnicians.map(technician => String(technician.id))
    setValue(merged.sort((a, b) => order.indexOf(String(a.id)) - order.indexOf(String(b.id))))
  }

  const remove = (technicians, stillCovered) => {
    const removed = idsOf(technicians)
    setValue(value.filter(technician => {
      const id = String(technician.id)
      return !removed.has(id) || stillCovered.has(id)
    }))
  }

  const togglePartner = partner => {
    const next = new Set(selectedPartners)
    const id   = String(partner.id)
    const on   = !next.has(id)
    on ? next.add(id) : next.delete(id)
    setSelectedPartners(next)
    on ? add(techniciansOfPartner(partner)) : remove(techniciansOfPartner(partner), coveredBy(next, selectedTeams))
  }

  const allPartnersOn = partners.length > 0 && partners.every(partner => selectedPartners.has(String(partner.id)))

  const toggleAllPartners = () => {
    const everyone = partners.flatMap(techniciansOfPartner)

    if (allPartnersOn) {
      setSelectedPartners(new Set())
      remove(everyone, coveredBy(new Set(), selectedTeams))
    } else {
      setSelectedPartners(new Set(partners.map(partner => String(partner.id))))
      add(everyone)
    }
  }

  const toggleTeam = (team, index) => {
    const next = new Set(selectedTeams)
    const on   = !next.has(index)
    on ? next.add(index) : next.delete(index)
    setSelectedTeams(next)
    on ? add(team.values) : remove(team.values, coveredBy(selectedPartners, next))
  }

  const submit = <input type="submit" value={props.previewLabel} className="btn btn-primary ml-1" id="preview-button" />

  return(
    <>
      {partners.length > 0 &&
        <Row label={I18n.t("users.timesheets.partner")}>
          <Chips>
            <Chip
              label    = {I18n.t("users.timesheets.all_partners")}
              selected = {allPartnersOn}
              onClick  = {toggleAllPartners}
            />
            {partners.map(partner => (
              <Chip
                key      = {partner.id}
                label    = {partner.name}
                selected = {selectedPartners.has(String(partner.id))}
                onClick  = {() => togglePartner(partner)}
              />
            ))}
          </Chips>
        </Row>
      }

      <Row label={I18n.t("users.timesheets.team")}>
        <Chips>
          {teams.map((team, index) => (
            <Chip
              key      = {team.name}
              label    = {index === 0 ? I18n.t("users.timesheets.all_teams") : team.name}
              selected = {selectedTeams.has(index)}
              onClick  = {() => toggleTeam(team, index)}
            />
          ))}
        </Chips>
      </Row>

      <Row label={I18n.t("users.timesheets.selection")} className="timesheet__filters__row--center">
        <ComboboxMulti
          fieldName = "technicians"
          prompt    = {props.prompt}
          options   = {allTechnicians}
          value     = {value}
          onChange  = {setValue}
          onClear   = {() => setValue([])}
        />
      </Row>

      {!props.technicianRowsOnly && <>
        <Row label={I18n.t("users.timesheets.event_type")} className="timesheet__filters__row--center">
          <div className="d-flex a-i-center j-c-between">
            <Chips>
              {types.map(option => (
                <Chip
                  key      = {option.id}
                  label    = {option.name}
                  selected = {type === option.id}
                  onClick  = {() => setType(option.id)}
                />
              ))}
            </Chips>

            {!tripsEnabled && submit}
          </div>
        </Row>

        {tripsEnabled &&
          <Row label={I18n.t("users.timesheets.trips")} className="timesheet__filters__row--center">
            <div className="d-flex a-i-center j-c-between">
              <Chips>
                <Chip label={I18n.t("users.timesheets.answer_yes")} selected={trips}  onClick={() => setTrips(true)} />
                <Chip label={I18n.t("users.timesheets.answer_no")}  selected={!trips} onClick={() => setTrips(false)} />
              </Chips>

              {submit}
            </div>
          </Row>
        }

        <input type="hidden" name="type"      value={type} readOnly />
        <input type="hidden" name="trips"     value={trips ? 'true' : 'false'} readOnly />
      </>}
      {[...selectedPartners].map(id => (
        <input key={id} type="hidden" name="partners[]" value={id} readOnly />
      ))}
    </>
  )
}

export default TechnicianFilters
